import React, { type ReactNode, useContext } from 'react';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { SearchPageContext } from '@/app/components/search/components/SearchPageContainer/SearchPageContext.ts';
import { useSearchDealsPageData } from '@/app/components/search/searchDealsPageProvider.tsx';
import TranslateSafeText from '@/app/common/components/TranslateSafeText';
import type { DealCard } from '../../../utils/buildDealCards/dealCardTypes';
import { getLocationArea, getLocationParts } from './utils';
import { LocationPart, LocationSeparator, TopText, ViewOnMapButton } from './styled';

type LocationTextProps = {
  dealCard: DealCard;
  isOverlay: boolean;
};

const Sep = () => <LocationSeparator>·</LocationSeparator>;

const LocationText = ({ dealCard, isOverlay }: LocationTextProps) => {
  const { t } = useTranslation();
  const { localization, searchInfoDetailData } = usePageWrapperData();
  const { isMobile } = useCommonClient();
  const { listView } = useContext(SearchPageContext);
  const { city, distance } = getLocationParts(dealCard, localization, t);
  const area = getLocationArea(dealCard, searchInfoDetailData?.destination);
  const viewOnMapLabel = t('vacation.oss.components.searchresults.tour_tile.view_on_map');
  const hotelId = dealCard?.product?.accommodation?.hotelId;
  const { handleViewMapClick } = useSearchDealsPageData();

  if (isOverlay) return null;

  const mapButton = (label: ReactNode) => (
    <ViewOnMapButton data-view-on-map={hotelId} onClick={(e) => handleViewMapClick?.(e, hotelId)}>
      {label}
    </ViewOnMapButton>
  );

  const getDistanceContent = (): ReactNode => {
    // map view → plain distance, no map action
    if (!listView) return <TranslateSafeText>{distance}</TranslateSafeText>;
    // mobile · list view → distance itself is the map trigger
    if (isMobile) return mapButton(distance);
    // desktop · list view → explicit "view on map" button after the distance
    return (
      <>
        <TranslateSafeText>{distance}</TranslateSafeText>
        <Sep />
        {mapButton(viewOnMapLabel)}
      </>
    );
  };

  // One line when it fits: city · area · distance. When it does not, the text wraps after the place, which is
  // never split ("Costa Adeje · Tenerife"), or within the distance.
  return (
    <TopText variant="BodySmall" color="textSecondary">
      <LocationPart>
        <TranslateSafeText>{city}</TranslateSafeText>
        {area && (
          <>
            <Sep />
            <TranslateSafeText>{area}</TranslateSafeText>
          </>
        )}
      </LocationPart>
      <Sep />
      {getDistanceContent()}
    </TopText>
  );
};

export default LocationText;
